import { useEffect, useRef, useState, useCallback } from "react";

const SCROLL_IDLE_DELAY = 160; // ms to wait before snapping
const SNAP_THRESHOLD = 120; // px distance to trigger snap
const PROGRAMMATIC_SCROLL_COOLDOWN = 400; // ms to wait after programmatic scroll

export interface MatchRect {
  pageNumber: number;
  rect: { x: number; y: number; w: number; h: number };
}

export interface ScrollAnchor {
  pageNumber: number;
  pageRatio: number; // 0-1, how far down the page
}

export function usePageVisibilityObserver(
  numPages: number,
  onPageChange: (page: number) => void
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const [currentPage, setCurrentPage] = useState(1);

  const stableOnPageChange = useCallback(onPageChange, []);

  // Capture scroll anchor - returns current viewport position relative to page
  const captureScrollAnchor = useCallback((): ScrollAnchor | null => {
    const container = containerRef.current;
    if (!container) return null;

    const containerRect = container.getBoundingClientRect();
    const viewportCenter = containerRect.top + containerRect.height / 2;

    // Find page at viewport center
    let anchorPage = currentPage;
    let anchorRatio = 0.5;

    for (let i = 0; i < pageRefs.current.length; i++) {
      const pageRef = pageRefs.current[i];
      if (!pageRef) continue;

      const pageRect = pageRef.getBoundingClientRect();
      
      // Check if viewport center is within this page
      if (viewportCenter >= pageRect.top && viewportCenter <= pageRect.bottom) {
        anchorPage = i + 1;
        anchorRatio = (viewportCenter - pageRect.top) / pageRect.height;
        break;
      }
    }

    return { pageNumber: anchorPage, pageRatio: anchorRatio };
  }, [currentPage]);

  // Restore scroll anchor - scrolls to saved position instantly
  const restoreScrollAnchor = useCallback((anchor: ScrollAnchor) => {
    const container = containerRef.current;
    const targetPage = pageRefs.current[anchor.pageNumber - 1];
    if (!container || !targetPage) return;

    // Disable snap assist during restoration
    isProgrammaticScrollRef.current = true;

    const containerRect = container.getBoundingClientRect();
    const pageRect = targetPage.getBoundingClientRect();
    
    // Calculate where the anchor point should be
    const anchorPointInPage = pageRect.top + (pageRect.height * anchor.pageRatio);
    const viewportCenter = containerRect.top + containerRect.height / 2;
    
    // Scroll to position the anchor at viewport center
    const scrollDelta = anchorPointInPage - viewportCenter;
    container.scrollTop += scrollDelta;

    // Re-enable snap assist after brief delay
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 50);
  }, []);

  // Scroll snap assist - runs when user stops scrolling
  const runSnapAssist = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    let bestPage = 1;
    let bestDistance = Infinity;

    // Find nearest page to viewport top
    pageRefs.current.forEach((pageRef, index) => {
      if (pageRef) {
        const pageRect = pageRef.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const distance = Math.abs(pageRect.top - containerRect.top);

        if (distance < bestDistance) {
          bestDistance = distance;
          bestPage = index + 1;
        }
      }
    });

    // Snap only if within threshold
    if (bestDistance < SNAP_THRESHOLD && bestDistance > 2) {
      const target = pageRefs.current[bestPage - 1];
      if (target) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const scrollOffset = targetRect.top - containerRect.top;
        
        container.scrollTo({
          top: container.scrollTop + scrollOffset,
          behavior: "smooth",
        });
      }
    }

    // Update current page
    if (bestPage !== currentPage) {
      setCurrentPage(bestPage);
      stableOnPageChange(bestPage);
    }
  }, [currentPage, stableOnPageChange]);

  // Handle scroll events with debounce
  const handleScroll = useCallback(() => {
    // Skip snap assist during programmatic scrolling
    if (isProgrammaticScrollRef.current) return;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      runSnapAssist();
    }, SCROLL_IDLE_DELAY);
  }, [runSnapAssist]);

  // Programmatic scroll to a specific page (used by thumbnails/toolbar)
  const scrollToPage = useCallback((page: number) => {
    const container = containerRef.current;
    const target = pageRefs.current[page - 1];
    if (!container || !target) return;

    // Disable snap assist during programmatic scroll
    isProgrammaticScrollRef.current = true;

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const scrollOffset = targetRect.top - containerRect.top;

    container.scrollTo({
      top: container.scrollTop + scrollOffset,
      behavior: "smooth",
    });

    // Update current page immediately
    setCurrentPage(page);
    stableOnPageChange(page);

    // Re-enable snap assist after scroll animation completes
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, PROGRAMMATIC_SCROLL_COOLDOWN);
  }, [stableOnPageChange]);

  // Scroll to a specific match rect and center it vertically
  const scrollToMatchRect = useCallback((match: MatchRect, zoom: number) => {
    const container = containerRef.current;
    const target = pageRefs.current[match.pageNumber - 1];
    if (!container || !target) return;

    // Disable snap assist during programmatic scroll
    isProgrammaticScrollRef.current = true;

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    // Calculate the match position in viewport coordinates
    const matchYInPage = match.rect.y * zoom;
    const matchHeight = match.rect.h * zoom;
    const matchCenterY = matchYInPage + matchHeight / 2;

    // Calculate target scroll position to center the match
    const pageTopInContainer = targetRect.top - containerRect.top + container.scrollTop;
    const targetScrollTop = pageTopInContainer + matchCenterY - containerRect.height / 2;

    container.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: "smooth",
    });

    // Update current page immediately
    setCurrentPage(match.pageNumber);
    stableOnPageChange(match.pageNumber);

    // Re-enable snap assist after scroll animation completes
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, PROGRAMMATIC_SCROLL_COOLDOWN);
  }, [stableOnPageChange]);

  // IntersectionObserver for page detection during active scrolling
  useEffect(() => {
    if (!numPages || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let mostVisiblePage = currentPage;
        let maxRatio = 0;

        entries.forEach((entry) => {
          const pageNumber = Number(entry.target.getAttribute("data-page-number"));
          if (entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            mostVisiblePage = pageNumber;
          }
        });

        if (mostVisiblePage !== currentPage && maxRatio > 0) {
          setCurrentPage(mostVisiblePage);
          stableOnPageChange(mostVisiblePage);
        }
      },
      {
        root: containerRef.current,
        threshold: Array.from({ length: 21 }, (_, i) => i / 20),
      }
    );

    pageRefs.current.forEach((page) => {
      if (page) observer.observe(page);
    });

    return () => observer.disconnect();
  }, [numPages, currentPage, stableOnPageChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return {
    containerRef,
    pageRefs,
    currentPage,
    handleScroll,
    scrollToPage,
    scrollToMatchRect,
    captureScrollAnchor,
    restoreScrollAnchor,
  };
}
